import Axios from "axios";

export interface ReportExportSchedule {
    id: number;
    name: string;
    dashboardId: string;
    visualizationId?: string;
    schedule: string;
    exportFormat: string;
    includeCoverSheet: boolean;
    enabled: boolean;
    deliveryType: string;
    deliveryConfigId?: number;
    destinationPath?: string;
    fileNamePattern?: string;
    lastExecutionTime?: string;
    created?: string;
    createdBy?: string;
}

export interface ReportExportScheduleRequest {
    name: string;
    dashboardId: string;
    visualizationId?: string;
    schedule: string;
    exportFormat: string;
    includeCoverSheet: boolean;
    enabled: boolean;
    deliveryType: string;
    deliveryConfigId?: number;
    destinationPath?: string;
    fileNamePattern?: string;
}

export interface ReportExportHistory {
    id: number;
    scheduleId: number;
    executionStart: string;
    executionEnd?: string;
    status: string;
    filePath?: string;
    fileSize?: number;
    recordCount?: number;
    errorMessage?: string;
    deliveryStatus?: string;
    deliveryAttempts: number;
}

export interface SftpConfig {
    id: number;
    name: string;
    host: string;
    port: number;
    username: string;
    authType: string;
    privateKeyPath?: string;
    created?: string;
    createdBy?: string;
}

export interface SftpConfigRequest {
    id?: number;
    name: string;
    host: string;
    port: number;
    username: string;
    password?: string;
    authType?: string;
    privateKeyPath?: string;
    privateKeyPassphrase?: string;
}

export interface S3Config {
    id: number;
    name: string;
    bucketName: string;
    region: string;
    accessKeyId?: string;
    roleArn?: string;
    externalId?: string;
    created?: string;
    createdBy?: string;
}

export interface S3ConfigRequest {
    id?: number;
    name: string;
    bucketName: string;
    region: string;
    accessKeyId?: string;
    secretAccessKey?: string;
    roleArn?: string;
    externalId?: string;
}

export interface ConnectionTestResponse {
    success: boolean;
    message: string;
}

const BASE_URL = "/report-export";

export const reportExportService = {
    // Schedules
    async listSchedules(dashboardId?: string): Promise<ReportExportSchedule[]> {
        const response = await Axios.get<ReportExportSchedule[]>(`${BASE_URL}/schedules`, {
            params: dashboardId ? { dashboardId } : undefined
        });
        return response.data;
    },

    async getScheduleById(id: number): Promise<ReportExportSchedule> {
        const response = await Axios.get<ReportExportSchedule>(`${BASE_URL}/schedules/${id}`);
        return response.data;
    },

    async createSchedule(request: ReportExportScheduleRequest): Promise<ReportExportSchedule> {
        const response = await Axios.post<ReportExportSchedule>(`${BASE_URL}/schedules`, request);
        return response.data;
    },

    async updateSchedule(id: number, request: ReportExportScheduleRequest): Promise<ReportExportSchedule> {
        const response = await Axios.put<ReportExportSchedule>(`${BASE_URL}/schedules/${id}`, request);
        return response.data;
    },

    async deleteSchedule(id: number): Promise<void> {
        await Axios.delete(`${BASE_URL}/schedules/${id}`);
    },

    async executeSchedule(id: number): Promise<{ message: string; historyId: number }> {
        const response = await Axios.post<{ message: string; historyId: number }>(`${BASE_URL}/schedules/${id}/execute`);
        return response.data;
    },

    async getScheduleHistory(id: number, limit = 20): Promise<ReportExportHistory[]> {
        const response = await Axios.get<ReportExportHistory[]>(`${BASE_URL}/schedules/${id}/history`, {
            params: { limit }
        });
        return response.data;
    },

    // SFTP Configs
    async listSftpConfigs(): Promise<SftpConfig[]> {
        const response = await Axios.get<SftpConfig[]>(`${BASE_URL}/sftp-configs`);
        return response.data;
    },

    async createSftpConfig(request: SftpConfigRequest): Promise<SftpConfig> {
        const response = await Axios.post<SftpConfig>(`${BASE_URL}/sftp-configs`, request);
        return response.data;
    },

    async updateSftpConfig(id: number, request: SftpConfigRequest): Promise<SftpConfig> {
        const response = await Axios.put<SftpConfig>(`${BASE_URL}/sftp-configs/${id}`, request);
        return response.data;
    },

    async deleteSftpConfig(id: number): Promise<void> {
        await Axios.delete(`${BASE_URL}/sftp-configs/${id}`);
    },

    async testSftpConnection(request: SftpConfigRequest): Promise<ConnectionTestResponse> {
        const response = await Axios.post<ConnectionTestResponse>(`${BASE_URL}/test-sftp`, request);
        return response.data;
    },

    // S3 Configs
    async listS3Configs(): Promise<S3Config[]> {
        const response = await Axios.get<S3Config[]>(`${BASE_URL}/s3-configs`);
        return response.data;
    },

    async createS3Config(request: S3ConfigRequest): Promise<S3Config> {
        const response = await Axios.post<S3Config>(`${BASE_URL}/s3-configs`, request);
        return response.data;
    },

    async updateS3Config(id: number, request: S3ConfigRequest): Promise<S3Config> {
        const response = await Axios.put<S3Config>(`${BASE_URL}/s3-configs/${id}`, request);
        return response.data;
    },

    async deleteS3Config(id: number): Promise<void> {
        await Axios.delete(`${BASE_URL}/s3-configs/${id}`);
    },

    async testS3Connection(request: S3ConfigRequest): Promise<ConnectionTestResponse> {
        const response = await Axios.post<ConnectionTestResponse>(`${BASE_URL}/test-s3`, request);
        return response.data;
    }
};
